import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircleIcon, CheckCircle2Icon, DownloadIcon, FileArchiveIcon, FolderInputIcon, FolderOpenIcon, Loader2Icon, RotateCcwIcon, SearchXIcon, StarIcon, Trash2Icon, UploadIcon, XCircleIcon, XIcon } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Toolbar } from './Toolbar';
import { FileList } from './FileList';
import { FileGrid } from './FileGrid';
import { DetailsPanel } from './DetailsPanel';
import { PreviewOverlay } from './PreviewOverlay';
import { PromptDialog } from './PromptDialog';
import { MoveDialog } from './MoveDialog';
import { ConfirmDialog } from './ConfirmDialog';
import type { ItemActions } from './ItemMenu';
import { useFileManager } from '../../hooks/useFileManager';
import { typeFromExtension } from '../../utils/fileTypes';
import { formatSize } from '../../utils/format';
import type { FsNode, Layout, PanelStatus } from '../../types/files';
type DialogState = {
  kind: 'none';
} | {
  kind: 'newFolder';
} | {
  kind: 'rename';
  node: FsNode;
} | {
  kind: 'move';
  ids: string[];
} | {
  kind: 'trash';
  ids: string[];
} | {
  kind: 'deleteForever';
  ids: string[];
} | {
  kind: 'emptyTrash';
};
interface FileManagerProps {
  initialStatus: PanelStatus;
  emptyFiles: boolean;
  compact: boolean;
  defaultLayout: Layout;
}
export function FileManager({
  initialStatus,
  emptyFiles,
  compact,
  defaultLayout
}: FileManagerProps) {
  const fm = useFileManager({
    initialStatus,
    emptyFiles,
    defaultLayout
  });
  const [dialog, setDialog] = useState<DialogState>({
    kind: 'none'
  });
  const [draggingIds, setDraggingIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isTrash = fm.view === 'trash';
  const hasSelection = fm.selectedIds.length > 0;
  const nameOf = (ids: string[]) => fm.nodes.filter(node => ids.includes(node.id)).map(node => node.name);
  const clearSelection = fm.clearSelection;
  const previewOpen = Boolean(fm.previewFile);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dialog.kind === 'none' && !previewOpen) {
        clearSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog.kind, previewOpen, clearSelection]);
  const download = (ids: string[]) => {
    const count = fm.startZipDownload(ids);
    if (count === 0) toast.error('Nothing to download — that folder is empty');
  };
  const actions: ItemActions = {
    onOpen: node => fm.openNode(node),
    onDetails: node => fm.setActiveId(node.id),
    onDownload: node => download([node.id]),
    onToggleStar: node => {
      fm.toggleStar(node.id);
      toast.success(node.starred ? 'Star removed' : `“${node.name}” starred`);
    },
    onRename: node => setDialog({
      kind: 'rename',
      node
    }),
    onMove: node => setDialog({
      kind: 'move',
      ids: [node.id]
    }),
    onTrash: node => setDialog({
      kind: 'trash',
      ids: [node.id]
    }),
    onRestore: node => {
      fm.restoreNodes([node.id]);
      toast.success(`“${node.name}” restored`);
    },
    onDeleteForever: node => setDialog({
      kind: 'deleteForever',
      ids: [node.id]
    })
  };
  const handleDropOnFolder = (targetId: string | null) => {
    const ids = draggingIds;
    setDraggingIds([]);
    if (ids.length === 0) return;
    const moved = fm.moveNodes(ids, targetId);
    if (moved > 0) {
      toast.success(`Moved ${moved} ${moved === 1 ? 'item' : 'items'}`);
    } else {
      toast.error("A folder can't be moved inside itself");
    }
  };
  const handleUploadChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const incoming = Array.from(files).map(file => {
      const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'txt';
      return {
        name: file.name.replace(/\.[^.]+$/, ''),
        extension,
        type: typeFromExtension(extension),
        sizeKb: Math.max(1, Math.round(file.size / 1024))
      };
    });
    const added = fm.uploadFiles(incoming);
    toast.success(`${added} ${added === 1 ? 'file' : 'files'} uploaded`);
  };
  return <section aria-label="Loan document library" className="flex h-[min(88vh,880px)] w-full flex-col overflow-hidden rounded bg-white shadow-sm ring-1 ring-slate-200" data-id="element-108" data-mp="3tfbuiz676f0k">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-109" data-mp="3jhnqfimpbv5q">
        <div className="flex items-center gap-2.5" data-id="element-110" data-mp="35rbuf8mzxc9b">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-[#eef4ff] text-[#0176d3]" data-id="element-111" data-mp="35ofwvlf28ple">
            <FolderOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-112" data-mp="2vs336njimbqn" />
          </span>
          <div data-id="element-113" data-mp="2vu60ztqwt95x">
            <h1 className="text-base font-semibold text-slate-900" data-id="element-114" data-mp="30r67g5ro8dxk">
              Loan Document Library
            </h1>
            <p className="text-xs text-slate-500" data-id="element-115" data-mp="2h4upng2qhubi">
              {fm.counts.all} files in {fm.counts.folders} folders ·{' '}
              {formatSize(fm.usedKb)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-116" data-mp="2r3twct3xvfvv">
          {isTrash ? <button type="button" onClick={() => setDialog({
          kind: 'emptyTrash'
        })} disabled={fm.counts.trash === 0} className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2" data-id="element-117" data-mp="2m3xrysrbxmfs">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-118" data-mp="3jtanzojqisr1" />
              Empty Recycle Bin
            </button> : <button type="button" onClick={() => download(fm.rows.length > 0 ? fm.rows.map(row => row.id) : [])} disabled={fm.rows.length === 0} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-[#0176d3] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-119" data-mp="3euza92qbjy3j">
              <FileArchiveIcon className="h-4 w-4" aria-hidden="true" data-id="element-120" data-mp="dfrjne8qnz9r" />
              Download This View
            </button>}
        </div>
      </header>

      {fm.download.status !== 'idle' && <div role="status" aria-live="polite" className={['flex items-center gap-3 border-b px-4 py-2.5 text-sm', fm.download.status === 'complete' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-sky-200 bg-sky-50 text-sky-900'].join(' ')} data-id="element-121" data-mp="ndnsfze7ei4p">
          {fm.download.status === 'zipping' ? <>
              <Loader2Icon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" data-id="element-122" data-mp="2cujc7siv4tvd" />
              <div className="min-w-0 flex-1" data-id="element-123" data-mp="22xwwtj9b2v0q">
                <p className="font-medium" data-id="element-124" data-mp="22z80lj2r42ru">
                  Compressing {fm.download.fileCount} files (
                  {formatSize(fm.download.sizeKb)})…
                </p>
                <div className="mt-1.5 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-sky-200" role="progressbar" aria-valuenow={fm.download.progress} aria-valuemin={0} aria-valuemax={100} aria-label="Zip progress" data-id="element-125" data-mp="2vndm481mpvsq">
                  <div className="h-full rounded-full bg-[#0176d3] transition-all duration-300" style={{
              width: `${fm.download.progress}%`
            }} data-id="element-126" data-mp="1f5ptu192rpsc" />
                </div>
              </div>
            </> : <>
              <CheckCircle2Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-127" data-mp="24u50wqrwpldx" />
              <p className="min-w-0 flex-1" data-id="element-128" data-mp="1qy1a77g8rv67">
                <span className="font-medium" data-id="element-129" data-mp="1uxs5nogpqzh2">Download ready.</span>{' '}
                <span className="break-all" data-id="element-130" data-mp="1v04pk7617jwr">{fm.download.name}</span> ·{' '}
                {fm.download.fileCount} files, {formatSize(fm.download.sizeKb)}
              </p>
            </>}
          <button type="button" onClick={fm.dismissDownload} aria-label="Dismiss download status" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-131" data-mp="2notobzjm7d5c">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-132" data-mp="36cypr0csum0z" />
          </button>
        </div>}

      {hasSelection && <div className="flex flex-col gap-2 border-b border-[#0176d3]/30 bg-[#eef4ff] px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between" data-id="element-133" data-mp="uxgf2w1f0h1g">
          <p className="text-sm font-medium text-[#014486]" data-id="element-134" data-mp="px2e1xy8e7cx">
            {fm.selectedIds.length} selected · {fm.selectedFiles.length} files ·{' '}
            {formatSize(fm.selectedSizeKb)}
          </p>
          <div className="flex flex-wrap items-center gap-2" data-id="element-135" data-mp="zrasxiuca1sm">
            {isTrash ? <>
                <button type="button" onClick={() => {
            const count = fm.selectedIds.length;
            fm.restoreNodes(fm.selectedIds);
            toast.success(`${count} ${count === 1 ? 'item' : 'items'} restored`);
          }} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-136" data-mp="70anr4k9urzd">
                  <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="3hhrjjrc797bz" />
                  Restore
                </button>
                <button type="button" onClick={() => setDialog({
            kind: 'deleteForever',
            ids: fm.selectedIds
          })} className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2" data-id="element-138" data-mp="3cpqaeeabvwq6">
                  <XCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-139" data-mp="2ou5iapqg2esa" />
                  Delete forever
                </button>
              </> : <>
                <button type="button" onClick={() => download(fm.selectedIds)} disabled={fm.download.status === 'zipping'} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-140" data-mp="n1h9m0v16z54">
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-141" data-mp="bn5tqlwv1x91" />
                  Download as .zip
                </button>
                <button type="button" onClick={() => setDialog({
            kind: 'move',
            ids: fm.selectedIds
          })} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-142" data-mp="gmigl6nqf4bm">
                  <FolderInputIcon className="h-4 w-4" aria-hidden="true" data-id="element-143" data-mp="220wm7t0phrtq" />
                  Move
                </button>
                <button type="button" onClick={() => {
            fm.selectedIds.forEach(id => fm.toggleStar(id));
            toast.success('Stars updated');
          }} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-144" data-mp="3t3dlukrrjn8h">
                  <StarIcon className="h-4 w-4" aria-hidden="true" data-id="element-145" data-mp="3o52b87tn98p7" />
                  Star
                </button>
                <button type="button" onClick={() => setDialog({
            kind: 'trash',
            ids: fm.selectedIds
          })} className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2" data-id="element-146" data-mp="3jbr4s7squtxt">
                  <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-147" data-mp="2maqy8vm2exm6" />
                  Delete
                </button>
              </>}
            <button type="button" onClick={fm.clearSelection} className="rounded px-2 py-1.5 text-sm font-medium text-[#014486] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-148" data-mp="2ck62rh1ou97h">
              Clear
            </button>
          </div>
        </div>}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row" data-id="element-149" data-mp="2qcvorlh7ve4l">
        <Sidebar nodes={fm.nodes} view={fm.view} folderId={fm.folderId} counts={fm.counts} usedKb={fm.usedKb} onChangeView={fm.changeView} onGoToFolder={fm.goToFolder} onDropOnFolder={handleDropOnFolder} draggingIds={draggingIds} data-id="element-150" data-mp="2v8ayc186ynv7" />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-id="element-151" data-mp="203oe9401o2rq">
          <Toolbar view={fm.view} breadcrumbs={fm.breadcrumbs} query={fm.query} activeTypes={fm.activeTypes} typeCounts={fm.typeCounts} sort={fm.sort} layout={fm.layout} resultCount={fm.rows.length} onQueryChange={fm.setQuery} onToggleType={fm.toggleType} onClearFilters={fm.clearFilters} onSortChange={fm.setSort} onLayoutChange={fm.setLayout} onGoToFolder={fm.goToFolder} onNewFolder={() => setDialog({
          kind: 'newFolder'
        })} onUpload={() => fileInputRef.current?.click()} onRefresh={fm.refresh} data-id="element-152" data-mp="1va3htge3ngun" />

          <div className="min-h-0 flex-1 overflow-auto" data-id="element-153" data-mp="xoo5p8g3j64c">
            {fm.status === 'loading' && <LoadingState data-id="element-154" data-mp="sn8ppusrzpst" />}

            {fm.status === 'error' && <EmptyState icon={AlertCircleIcon} iconClass="text-red-600" title="We couldn't load this library" body="The Files service didn't respond. Your documents are safe — try again." action={<button type="button" onClick={fm.retry} className="rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-156" data-mp="3izzwf64ermqe">
                    Retry
                  </button>} data-id="element-155" data-mp="34bvkckzv29gl" />}

            {fm.status === 'ready' && fm.rows.length === 0 && <EmptyState icon={fm.query.trim().length > 0 || fm.activeTypes.length > 0 ? SearchXIcon : isTrash ? Trash2Icon : FolderOpenIcon} iconClass="text-slate-400" title={fm.query.trim().length > 0 || fm.activeTypes.length > 0 ? 'No items match your filters' : isTrash ? 'Recycle Bin is empty' : 'This folder is empty'} body={fm.query.trim().length > 0 || fm.activeTypes.length > 0 ? 'Try a different search term or clear the file type filters.' : isTrash ? 'Deleted files and folders will appear here for 30 days.' : 'Upload documents or create a folder to organize this loan package.'} action={fm.query.trim().length > 0 || fm.activeTypes.length > 0 ? <button type="button" onClick={fm.clearFilters} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-[#0176d3] transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-158" data-mp="2x98e0wbo7nec">
                      Clear filters
                    </button> : isTrash ? null : <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-159" data-mp="2x5px0nzh6rmo">
                      <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-160" data-mp="2e8f1negysici" />
                      Upload Files
                    </button>} data-id="element-157" data-mp="22mjhxluf93lx" />}

            {fm.status === 'ready' && fm.rows.length > 0 && (fm.layout === 'list' ? <FileList nodes={fm.nodes} rows={fm.rows} selectedIds={fm.selectedIds} activeId={fm.activeNode?.id ?? null} allRowsSelected={fm.allRowsSelected} someRowsSelected={fm.someRowsSelected} sort={fm.sort} compact={compact} isTrash={isTrash} showLocation={fm.searching || fm.view !== 'files'} actions={actions} onToggleRow={fm.toggleRow} onToggleAll={fm.toggleAllRows} onSort={fm.toggleSort} onDragStart={node => setDraggingIds(fm.selectedIds.includes(node.id) ? fm.selectedIds : [node.id])} onDragEnd={() => setDraggingIds([])} onDropOnFolder={handleDropOnFolder} draggingIds={draggingIds} data-id="element-161" data-mp="2unef35ul2fi4" /> : <FileGrid nodes={fm.nodes} rows={fm.rows} selectedIds={fm.selectedIds} isTrash={isTrash} actions={actions} onToggleRow={fm.toggleRow} onDragStart={node => setDraggingIds(fm.selectedIds.includes(node.id) ? fm.selectedIds : [node.id])} onDragEnd={() => setDraggingIds([])} onDropOnFolder={handleDropOnFolder} draggingIds={draggingIds} data-id="element-162" data-mp="1rusg1u5p7hgl" />)}
          </div>
        </div>

        {fm.activeNode && !fm.previewFile && <DetailsPanel node={fm.activeNode} nodes={fm.nodes} onClose={() => fm.setActiveId(null)} onDownload={node => download([node.id])} onToggleStar={node => fm.toggleStar(node.id)} onRename={node => setDialog({
        kind: 'rename',
        node
      })} onMove={node => setDialog({
        kind: 'move',
        ids: [node.id]
      })} onTrash={node => setDialog({
        kind: 'trash',
        ids: [node.id]
      })} onGoToFolder={fm.goToFolder} data-id="element-163" data-mp="2dl84tegikgng" />}
      </div>

      {fm.previewFile && <PreviewOverlay file={fm.previewFile} nodes={fm.nodes} position={fm.previewPosition} hasPrev={fm.hasPrevPreview} hasNext={fm.hasNextPreview} onPrev={() => fm.stepPreview(-1)} onNext={() => fm.stepPreview(1)} onClose={fm.closePreview} onDownload={node => download([node.id])} onToggleStar={node => fm.toggleStar(node.id)} onRename={node => setDialog({
      kind: 'rename',
      node
    })} onMove={node => setDialog({
      kind: 'move',
      ids: [node.id]
    })} onTrash={node => setDialog({
      kind: 'trash',
      ids: [node.id]
    })} onGoToFolder={fm.goToFolder} data-id="element-164" data-mp="23wq4eyc5cbkc" />}

      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={event => {
      handleUploadChange(event.target.files);
      event.target.value = '';
    }} data-id="element-165" data-mp="uo4ukbhrhh39" />

      {dialog.kind === 'newFolder' && <PromptDialog title="New folder" label="Folder name" confirmLabel="Create" initialValue="Untitled folder" onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={value => {
      fm.createFolder(value);
      setDialog({
        kind: 'none'
      });
      toast.success(`Folder “${value}” created`);
    }} data-id="element-166" data-mp="fyp7b5hfvtsb" />}

      {dialog.kind === 'rename' && <PromptDialog title="Rename" label="Name" confirmLabel="Save" initialValue={dialog.node.name} onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={value => {
      fm.renameNode(dialog.node.id, value);
      setDialog({
        kind: 'none'
      });
      toast.success('Renamed');
    }} data-id="element-167" data-mp="3ofwvuwb7mbtw" />}

      {dialog.kind === 'move' && <MoveDialog nodes={fm.nodes} movingIds={dialog.ids} onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={targetId => {
      const moved = fm.moveNodes(dialog.ids, targetId);
      setDialog({
        kind: 'none'
      });
      toast.success(`Moved ${moved} ${moved === 1 ? 'item' : 'items'}`);
    }} data-id="element-168" data-mp="b3wgf8mwgp4e" />}

      {dialog.kind === 'trash' && <ConfirmDialog title={`Move ${dialog.ids.length} ${dialog.ids.length === 1 ? 'item' : 'items'} to the Recycle Bin?`} description="Items stay recoverable for 30 days. Folders move with everything inside them, and compliance documents may need to be re-collected." items={nameOf(dialog.ids)} confirmLabel="Move to Recycle Bin" onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={() => {
      const count = dialog.ids.length;
      fm.trashNodes(dialog.ids);
      setDialog({
        kind: 'none'
      });
      toast.success(`${count} ${count === 1 ? 'item' : 'items'} moved to the Recycle Bin`);
    }} data-id="element-169" data-mp="yre52vavb735" />}

      {dialog.kind === 'deleteForever' && <ConfirmDialog title={`Permanently delete ${dialog.ids.length} ${dialog.ids.length === 1 ? 'item' : 'items'}?`} description="This cannot be undone. The files are removed from the loan record and from Salesforce storage." items={nameOf(dialog.ids)} confirmLabel="Delete forever" onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={() => {
      const count = dialog.ids.length;
      fm.deleteForever(dialog.ids);
      setDialog({
        kind: 'none'
      });
      toast.success(`${count} ${count === 1 ? 'item' : 'items'} permanently deleted`);
    }} data-id="element-170" data-mp="29hai7iy954cv" />}

      {dialog.kind === 'emptyTrash' && <ConfirmDialog title="Empty the Recycle Bin?" description={`All ${fm.counts.trash} items in the Recycle Bin will be permanently deleted. This cannot be undone.`} confirmLabel="Empty Recycle Bin" onCancel={() => setDialog({
      kind: 'none'
    })} onConfirm={() => {
      fm.emptyTrash();
      setDialog({
        kind: 'none'
      });
      toast.success('Recycle Bin emptied');
    }} data-id="element-171" data-mp="z1n1jh3lfejr" />}
    </section>;
}
function LoadingState() {
  return <div className="divide-y divide-slate-100" aria-busy="true" data-id="element-172" data-mp="1mr7mg5jb4lvo">
      <p className="sr-only" data-id="element-173" data-mp="378zz88ofehbq">Loading files</p>
      {Array.from({
      length: 8
    }).map((_, index) => <div key={index} className="flex items-center gap-3 px-4 py-3" data-id="element-174" data-mp="328udta8r1r23" data-mpi={index}>
          <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-slate-200" data-id="element-175" data-mp="32bq9st11e5nw" />
          <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-slate-200" data-id="element-176" data-mp="2xcderl00hsa8" />
          <div className="h-3 flex-1 animate-pulse rounded bg-slate-200" data-id="element-177" data-mp="2scr1jbk6oc1f" />
          <div className="hidden h-3 w-28 animate-pulse rounded bg-slate-200 sm:block" data-id="element-178" data-mp="3pz80smch8zpt" />
          <div className="h-3 w-14 animate-pulse rounded bg-slate-200" data-id="element-179" data-mp="3kzbw0kvyh497" />
        </div>)}
    </div>;
}
interface EmptyStateProps {
  icon: typeof FolderOpenIcon;
  iconClass: string;
  title: string;
  body: string;
  action: React.ReactNode;
}
function EmptyState({
  icon: Icon,
  iconClass,
  title,
  body,
  action
}: EmptyStateProps) {
  return <div className="flex flex-col items-center gap-3 px-4 py-16 text-center" data-id="element-180" data-mp="clwid12jq9a9">
      <Icon className={`h-8 w-8 ${iconClass}`} aria-hidden="true" data-id="element-181" data-mp="7s0lk1y0qsgo" />
      <div data-id="element-182" data-mp="2228h34m9dgio">
        <p className="text-sm font-semibold text-slate-900" data-id="element-183" data-mp="222i8mwmybvqh">{title}</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600" data-id="element-184" data-mp="270s8hkjmfkif">{body}</p>
      </div>
      {action}
    </div>;
}